import React, { useState, useEffect, useCallback, useRef } from 'react';
import AdminLayout from '@/components/layout/AdminLayout';
import { API } from '@/App';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import {
  BookOpen, Plus, Upload, Loader2, Trash2, Eye, Image, Music, MessageSquare,
  ChevronDown, ChevronRight, Save, Send, X, GripVertical, FileText, Wand2
} from 'lucide-react';

const CATEGORIES = [
  'general', 'nutrition', 'exercise', 'sleep', 'medication',
  'mindfulness', 'research', 'protocol', 'dopamine', 'gut_brain',
];

// ═══════════════════════════════════════════════════════════════════════════════
// COLLAPSIBLE SECTION (defined OUTSIDE editor to prevent remounting)
// ═══════════════════════════════════════════════════════════════════════════════
function Section({ id, label, icon: Icon, open, onToggle, count, children }) {
  return (
    <div className="rounded-lg border border-white/[0.06] overflow-hidden">
      <button onClick={() => onToggle(id)} className="w-full flex items-center gap-3 p-3 hover:bg-white/[0.02] transition-colors text-left" type="button">
        <Icon className="w-4 h-4 text-adm-accent" strokeWidth={1.25} />
        <span className="text-sm text-adm-primary flex-1">{label}</span>
        {count != null && <span className="text-[10px] text-adm-tertiary">{count}</span>}
        {open ? <ChevronDown className="w-3.5 h-3.5 text-adm-tertiary" /> : <ChevronRight className="w-3.5 h-3.5 text-adm-tertiary" />}
      </button>
      {open && <div className="px-3 pb-4 space-y-3 border-t border-white/[0.04] pt-3">{children}</div>}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// CONTENT LIST PAGE
// ═══════════════════════════════════════════════════════════════════════════════
const STATUS_FILTERS = [
  { value: 'all', label: 'All' },
  { value: 'draft', label: 'Draft' },
  { value: 'published', label: 'Published' },
  { value: 'archived', label: 'Archived' },
];

const STATUS_BADGE = {
  draft: 'bg-amber-500/15 text-amber-400 border-amber-500/20',
  published: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/20',
  archived: 'bg-white/5 text-white/40 border-white/10',
};

export default function ContentPublishing() {
  const [items, setItems] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [showEditor, setShowEditor] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');

  const fetchItems = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/knowledge/admin/content`);
      setItems(res.data || []);
    } catch { /* empty */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    fetchItems();
    axios.get(`${API}/users?role=subscriber`).then(r => {
      setSubscribers(Array.isArray(r.data) ? r.data : r.data?.users || []);
    }).catch(() => {});
  }, [fetchItems]);

  const openNew = () => { setEditingId(null); setShowEditor(true); };
  const openEdit = (id) => { setEditingId(id); setShowEditor(true); };
  const handleDelete = async (id) => {
    if (!confirm('Archive this content?')) return;
    await axios.delete(`${API}/knowledge/content/${id}`);
    toast.success('Archived');
    fetchItems();
  };

  const filteredItems = statusFilter === 'all'
    ? items
    : items.filter(it => (it.status || 'draft') === statusFilter);

  const statusCounts = items.reduce((acc, it) => {
    const s = it.status || 'draft';
    acc[s] = (acc[s] || 0) + 1;
    return acc;
  }, {});

  return (
    <AdminLayout>
      <div className="max-w-6xl mx-auto px-6 py-6 space-y-6" data-testid="content-publishing">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-['Outfit'] font-semibold text-adm-primary" data-testid="content-title">Knowledge Publishing</h1>
            <p className="text-xs text-adm-tertiary mt-1">Create and manage structured knowledge content for subscribers</p>
          </div>
          <Button onClick={openNew} className="bg-adm-accent/20 text-adm-accent hover:bg-adm-accent/30 border-0 text-xs" data-testid="new-content-btn">
            <Plus className="w-3.5 h-3.5 mr-1.5" /> New Content
          </Button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 text-adm-accent animate-spin" /></div>
        ) : items.length === 0 ? (
          <Card className="bg-adm-panel border-white/[0.06]">
            <CardContent className="p-12 text-center">
              <BookOpen className="w-10 h-10 text-adm-tertiary mx-auto mb-4" strokeWidth={1} />
              <p className="text-adm-secondary text-sm mb-2">No content yet</p>
              <Button onClick={openNew} variant="outline" className="border-white/10 text-adm-secondary text-xs">
                <Plus className="w-3 h-3 mr-1.5" /> Create First Topic
              </Button>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Status filter chips */}
            <div className="flex flex-wrap gap-2" data-testid="content-status-filter">
              {STATUS_FILTERS.map(f => {
                const count = f.value === 'all' ? items.length : (statusCounts[f.value] || 0);
                const active = statusFilter === f.value;
                return (
                  <button
                    key={f.value}
                    type="button"
                    onClick={() => setStatusFilter(f.value)}
                    data-testid={`content-status-${f.value}`}
                    className={`px-3 py-1.5 rounded-full text-[11px] border transition-colors ${
                      active
                        ? 'bg-adm-accent/15 text-adm-accent border-adm-accent/30'
                        : 'bg-white/[0.02] text-adm-tertiary border-white/10 hover:text-adm-secondary'
                    }`}
                  >
                    {f.label} <span className="ml-1 opacity-60">({count})</span>
                  </button>
                );
              })}
            </div>

            {filteredItems.length === 0 ? (
              <div className="text-center py-12 text-adm-tertiary text-xs" data-testid="content-empty-filter">
                No {statusFilter !== 'all' ? statusFilter : ''} content.
              </div>
            ) : (
              <div className="space-y-2">
                {filteredItems.map(item => (
                  <Card key={item.id} className={`bg-adm-panel border-white/[0.06] hover:border-white/10 transition-colors cursor-pointer ${(item.status === 'archived') ? 'opacity-60' : ''}`} onClick={() => openEdit(item.id)} data-testid={`content-item-${item.id}`}>
                    <CardContent className="p-4 flex items-center gap-4">
                      {item.cover_image ? (
                        <div className="w-16 h-12 rounded-lg overflow-hidden flex-shrink-0 bg-white/5">
                          <img src={item.cover_image.startsWith('http') ? item.cover_image : item.cover_image.startsWith('/api/') ? item.cover_image : `${API}/documents/file/${item.cover_image}`} alt="" className="w-full h-full object-cover" />
                        </div>
                      ) : (
                        <div className="w-16 h-12 rounded-lg bg-white/[0.04] flex items-center justify-center flex-shrink-0"><BookOpen className="w-5 h-5 text-adm-tertiary" strokeWidth={1} /></div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-adm-primary truncate">{item.title}</p>
                        <p className="text-[10px] text-adm-tertiary truncate">{item.context_line || item.subtitle || item.category}</p>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {item.slide_images?.length > 0 && <Badge variant="outline" className="border-white/10 text-adm-tertiary text-[9px]"><Image className="w-2.5 h-2.5 mr-0.5" />{item.slide_images.length}</Badge>}
                        {item.audio_url && <Badge variant="outline" className="border-white/10 text-adm-tertiary text-[9px]"><Music className="w-2.5 h-2.5" /></Badge>}
                        {item.comment_count > 0 && <Badge variant="outline" className="border-white/10 text-adm-tertiary text-[9px]"><MessageSquare className="w-2.5 h-2.5 mr-0.5" />{item.comment_count}</Badge>}
                        {item.engagement?.opened > 0 && <Badge variant="outline" className="border-white/10 text-adm-tertiary text-[9px]"><Eye className="w-2.5 h-2.5 mr-0.5" />{item.engagement.completed}/{item.engagement.opened}</Badge>}
                        {item.reactions_summary?.need_simpler > 0 && <Badge className="bg-amber-500/15 text-amber-400 border-amber-500/20 text-[9px]">Simpler needed</Badge>}
                        <Badge className={`text-[9px] ${STATUS_BADGE[item.status] || STATUS_BADGE.draft}`} data-testid={`content-status-badge-${item.id}`}>{item.status || 'draft'}</Badge>
                        {item.status !== 'archived' && (
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-adm-tertiary hover:text-rose-400" onClick={e => { e.stopPropagation(); handleDelete(item.id); }} data-testid={`content-archive-${item.id}`}>
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </>
        )}

        {showEditor && (
          <ContentEditor
            contentId={editingId}
            subscribers={subscribers}
            onClose={() => { setShowEditor(false); setEditingId(null); }}
            onSaved={() => { setShowEditor(false); setEditingId(null); fetchItems(); }}
          />
        )}
      </div>
    </AdminLayout>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// CONTENT EDITOR — All bugs fixed
// ═══════════════════════════════════════════════════════════════════════════════
function ContentEditor({ contentId, subscribers, onClose, onSaved }) {
  const [form, setForm] = useState({
    title: '', subtitle: '', context_line: '', category: 'general',
    tags: '', priority: 0, visibility_type: 'all', assigned_subscribers: [],
    takeaways: ['', '', ''], sections: [{ title: '', body: '' }],
    status: 'draft',
    relevance_tags: '', target_condition: '', target_phase: '',
    target_symptom_patterns: '', target_medication_patterns: '', priority_weight: 0,
  });
  const [slideImages, setSlideImages] = useState([]);
  const [audioUrl, setAudioUrl] = useState('');
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState(''); // '', 'uploading', 'converting', 'success', 'error'
  const [loadingExisting, setLoadingExisting] = useState(!!contentId);
  const [openSections, setOpenSections] = useState({ metadata: true, slides: true, audio: true, sections: true });
  const [articleMode, setArticleMode] = useState('manual'); // manual | paste
  const [pastedArticle, setPastedArticle] = useState('');
  const [autoStructuring, setAutoStructuring] = useState(false);

  const slideInputRef = useRef(null);
  const pptxInputRef = useRef(null);
  const audioInputRef = useRef(null);

  useEffect(() => {
    if (!contentId) { setLoadingExisting(false); return; }
    axios.get(`${API}/knowledge/admin/content/${contentId}`).then(r => {
      const d = r.data;
      setForm({
        title: d.title || '', subtitle: d.subtitle || '', context_line: d.context_line || '',
        category: d.category || 'general', tags: (d.tags || []).join(', '),
        priority: d.priority || 0, visibility_type: d.visibility_type || 'all',
        assigned_subscribers: d.assigned_subscribers || [],
        takeaways: d.takeaways?.length ? d.takeaways : ['', '', ''],
        sections: d.sections?.length ? d.sections : [{ title: '', body: '' }],
        status: d.status || 'draft',
        relevance_tags: (d.relevance_tags || []).join(', '),
        target_condition: d.target_condition || '',
        target_phase: d.target_phase || '',
        target_symptom_patterns: (d.target_symptom_patterns || []).join(', '),
        target_medication_patterns: (d.target_medication_patterns || []).join(', '),
        priority_weight: d.priority_weight || 0,
      });
      setSlideImages(d.slide_images || []);
      setAudioUrl(d.audio_url || '');
    }).catch(() => toast.error('Failed to load'))
      .finally(() => setLoadingExisting(false));
  }, [contentId]);

  const set = useCallback((k, v) => setForm(p => ({ ...p, [k]: v })), []);

  const toggleSection = useCallback((id) => {
    setOpenSections(p => ({ ...p, [id]: !p[id] }));
  }, []);

  // ── Save / Publish ──
  const [savedContentId, setSavedContentId] = useState(contentId);
  const effectiveContentId = savedContentId || contentId;

  const handleSave = async (publishNow) => {
    if (!form.title.trim()) { toast.error('Title required'); return; }
    setSaving(true);
    const status = publishNow ? 'published' : form.status === 'published' ? 'published' : 'draft';
    const fd = new FormData();
    fd.append('title', form.title); fd.append('subtitle', form.subtitle);
    fd.append('context_line', form.context_line); fd.append('category', form.category);
    fd.append('tags', form.tags); fd.append('priority', String(form.priority));
    fd.append('visibility_type', form.visibility_type);
    fd.append('assigned_subscribers', form.assigned_subscribers.join(','));
    fd.append('takeaways', JSON.stringify(form.takeaways.filter(t => t.trim())));
    fd.append('sections', JSON.stringify(form.sections.filter(s => s.title.trim() || s.body.trim())));
    fd.append('status', status);
    fd.append('relevance_tags', form.relevance_tags); fd.append('target_condition', form.target_condition);
    fd.append('target_phase', form.target_phase); fd.append('target_symptom_patterns', form.target_symptom_patterns);
    fd.append('target_medication_patterns', form.target_medication_patterns);
    fd.append('priority_weight', String(form.priority_weight));
    try {
      if (effectiveContentId) {
        await axios.put(`${API}/knowledge/content/${effectiveContentId}`, fd);
        toast.success(publishNow ? 'Published!' : 'Saved');
        if (publishNow) onSaved();
      } else {
        const res = await axios.post(`${API}/knowledge/content`, fd);
        const newId = res.data?.id;
        setSavedContentId(newId);
        toast.success('Draft saved — you can now upload slides and audio');
      }
    } catch (err) { toast.error(err.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  // ── Slide uploads (images) ──
  const handleSlideUpload = async (e) => {
    const files = e.target.files;
    if (!files?.length) return;
    if (!effectiveContentId) { toast.error('Save as draft first, then upload slides'); return; }
    setUploading(true); setUploadStatus('uploading');
    const fd = new FormData();
    for (const f of files) fd.append('files', f);
    try {
      const res = await axios.post(`${API}/knowledge/content/${effectiveContentId}/upload-slides`, fd);
      setSlideImages(res.data.slide_images || []);
      setUploadStatus('success');
      toast.success(`${res.data.slide_count} slides uploaded`);
    } catch { setUploadStatus('error'); toast.error('Slide upload failed'); }
    finally { setUploading(false); if (slideInputRef.current) slideInputRef.current.value = ''; }
  };

  // ── PPTX upload ──
  const handlePptxUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!effectiveContentId) { toast.error('Save as draft first, then upload PPTX'); return; }
    setUploading(true); setUploadStatus('converting');
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await axios.post(`${API}/knowledge/content/${effectiveContentId}/upload-pptx`, fd, { timeout: 120000 });
      setSlideImages(res.data.slide_images || []);
      setUploadStatus('success');
      toast.success(`PPTX converted: ${res.data.slide_count} slides`);
    } catch (err) {
      setUploadStatus('error');
      toast.error(err.response?.data?.detail || 'PPTX conversion failed');
    }
    finally { setUploading(false); if (pptxInputRef.current) pptxInputRef.current.value = ''; }
  };

  // ── Audio upload ──
  const handleAudioUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!effectiveContentId) { toast.error('Save as draft first, then upload audio'); return; }
    setUploading(true); setUploadStatus('uploading');
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await axios.post(`${API}/knowledge/content/${effectiveContentId}/upload-audio`, fd);
      setAudioUrl(res.data.audio_url || '');
      setUploadStatus('success');
      toast.success('Audio uploaded');
    } catch { setUploadStatus('error'); toast.error('Audio upload failed'); }
    finally { setUploading(false); if (audioInputRef.current) audioInputRef.current.value = ''; }
  };

  // ── Auto-structure article ──
  const handleAutoStructure = () => {
    if (!pastedArticle.trim()) { toast.error('Paste an article first'); return; }
    setAutoStructuring(true);

    const text = pastedArticle.trim();
    const lines = text.split('\n').filter(l => l.trim());
    const sections = [];

    // Try to detect headings (lines that are short + followed by longer content)
    let currentSection = { title: '', body: '' };
    const headingPatterns = /^(#{1,3}\s+|[A-Z][^.]{3,60}$|\d+\.\s+)/;

    for (const line of lines) {
      const trimmed = line.trim();
      if (headingPatterns.test(trimmed) && trimmed.length < 80) {
        if (currentSection.body.trim() || currentSection.title) {
          sections.push({ ...currentSection });
        }
        currentSection = { title: trimmed.replace(/^#{1,3}\s+/, '').replace(/^\d+\.\s+/, ''), body: '' };
      } else {
        currentSection.body += (currentSection.body ? '\n' : '') + trimmed;
      }
    }
    if (currentSection.body.trim() || currentSection.title) {
      sections.push(currentSection);
    }

    // If no headings detected, split into default structure
    if (sections.length <= 1) {
      const words = text.split(/\s+/);
      const chunkSize = Math.ceil(words.length / 5);
      const defaults = ['What is happening', 'Biological explanation', 'Why it matters in your case', 'What to observe', 'What improves over time'];
      const newSections = [];
      for (let i = 0; i < 5; i++) {
        const chunk = words.slice(i * chunkSize, (i + 1) * chunkSize).join(' ');
        if (chunk.trim()) {
          newSections.push({ title: defaults[i], body: chunk });
        }
      }
      set('sections', newSections.length > 0 ? newSections : [{ title: '', body: text }]);
    } else {
      set('sections', sections);
    }

    setArticleMode('manual');
    setPastedArticle('');
    setAutoStructuring(false);
    toast.success(`Article split into ${sections.length > 1 ? sections.length : 'structured'} sections — review and edit`);
  };

  // ── Takeaway helpers ──
  const updateTakeaway = useCallback((i, val) => {
    setForm(p => {
      const arr = [...p.takeaways];
      arr[i] = val;
      return { ...p, takeaways: arr };
    });
  }, []);
  const addTakeaway = () => { if (form.takeaways.length < 5) set('takeaways', [...form.takeaways, '']); };
  const removeTakeaway = (i) => set('takeaways', form.takeaways.filter((_, idx) => idx !== i));

  // ── Section helpers ──
  const updateSection = useCallback((i, field, val) => {
    setForm(p => {
      const arr = [...p.sections];
      arr[i] = { ...arr[i], [field]: val };
      return { ...p, sections: arr };
    });
  }, []);
  const addSection = () => { if (form.sections.length < 6) set('sections', [...form.sections, { title: '', body: '' }]); };
  const removeSection = (i) => set('sections', form.sections.filter((_, idx) => idx !== i));

  const toggleSub = (subId) => {
    set('assigned_subscribers', form.assigned_subscribers.includes(subId)
      ? form.assigned_subscribers.filter(s => s !== subId)
      : [...form.assigned_subscribers, subId]);
  };

  if (loadingExisting) return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-adm-surface border-white/10 max-w-3xl"><div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 text-adm-accent animate-spin" /></div></DialogContent>
    </Dialog>
  );

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-adm-surface border-white/10 max-w-3xl max-h-[90vh] overflow-y-auto p-0" data-testid="content-editor">
        <DialogHeader className="px-5 pt-5 pb-3 border-b border-white/[0.06]">
          <DialogTitle className="text-adm-primary font-['Work_Sans'] text-base">
            {contentId ? 'Edit Content' : 'New Knowledge Content'}
          </DialogTitle>
        </DialogHeader>

        <div className="px-5 py-4 space-y-3">
          {/* Title (always visible) */}
          <Input value={form.title} onChange={e => set('title', e.target.value)} placeholder="Topic title"
            className="bg-adm-panel border-white/10 text-adm-primary h-10 text-base font-medium" data-testid="content-title-input" />

          {/* Upload status banner */}
          {uploadStatus === 'converting' && (
            <div className="flex items-center gap-2 p-2.5 rounded-lg bg-purple-500/10 border border-purple-500/20 text-purple-400 text-xs">
              <Loader2 className="w-3.5 h-3.5 animate-spin" /> Converting PPTX slides... this may take a moment
            </div>
          )}
          {uploadStatus === 'uploading' && (
            <div className="flex items-center gap-2 p-2.5 rounded-lg bg-blue-500/10 border border-blue-500/20 text-blue-400 text-xs">
              <Loader2 className="w-3.5 h-3.5 animate-spin" /> Uploading...
            </div>
          )}
          {uploadStatus === 'success' && (
            <div className="flex items-center gap-2 p-2.5 rounded-lg bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-xs">
              Upload successful
            </div>
          )}
          {uploadStatus === 'error' && (
            <div className="flex items-center gap-2 p-2.5 rounded-lg bg-rose-500/10 border border-rose-500/20 text-rose-400 text-xs">
              Upload failed — try again or use a different format
            </div>
          )}

          {/* ═══ METADATA ═══ */}
          <Section id="metadata" label="Metadata & Assignment" icon={BookOpen} open={!!openSections.metadata} onToggle={toggleSection}>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-[10px] text-adm-tertiary">Subtitle</Label>
                <Input value={form.subtitle} onChange={e => set('subtitle', e.target.value)} className="bg-adm-panel border-white/10 text-adm-primary h-8 text-xs" placeholder="Optional subtitle" data-testid="subtitle-input" /></div>
              <div><Label className="text-[10px] text-adm-tertiary">Context Line</Label>
                <Input value={form.context_line} onChange={e => set('context_line', e.target.value)} className="bg-adm-panel border-white/10 text-adm-primary h-8 text-xs" placeholder="e.g. Relevant to your medication pattern" data-testid="context-line-input" /></div>
              <div><Label className="text-[10px] text-adm-tertiary">Category</Label>
                <Select value={form.category} onValueChange={v => set('category', v)}>
                  <SelectTrigger className="bg-adm-panel border-white/10 h-8 text-xs text-adm-primary"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-adm-panel border-white/10">{CATEGORIES.map(c => <SelectItem key={c} value={c} className="text-xs">{c}</SelectItem>)}</SelectContent>
                </Select></div>
              <div><Label className="text-[10px] text-adm-tertiary">Tags</Label>
                <Input value={form.tags} onChange={e => set('tags', e.target.value)} className="bg-adm-panel border-white/10 text-adm-primary h-8 text-xs" placeholder="comma-separated" data-testid="tags-input" /></div>
            </div>
            <div className="flex items-center gap-4">
              <Label className="text-[10px] text-adm-tertiary">Visibility</Label>
              <Select value={form.visibility_type} onValueChange={v => set('visibility_type', v)}>
                <SelectTrigger className="bg-adm-panel border-white/10 h-8 text-xs text-adm-primary w-40"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-adm-panel border-white/10">
                  <SelectItem value="all" className="text-xs">All Subscribers</SelectItem>
                  <SelectItem value="specific" className="text-xs">Specific Subscribers</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {form.visibility_type === 'specific' && (
              <div className="max-h-32 overflow-y-auto space-y-1 rounded-lg bg-white/[0.02] p-2">
                {subscribers.map(s => (
                  <label key={s.id} className="flex items-center gap-2 text-xs text-adm-secondary cursor-pointer py-1 px-1 hover:bg-white/[0.02] rounded">
                    <input type="checkbox" checked={form.assigned_subscribers.includes(s.id)} onChange={() => toggleSub(s.id)} className="rounded border-white/20 bg-transparent" />
                    {s.name} <span className="text-adm-tertiary text-[10px]">{s.email}</span>
                  </label>
                ))}
              </div>
            )}
          </Section>

          {/* ═══ RELEVANCE ═══ */}
          <Section id="relevance" label="Relevance Targeting" icon={Eye} open={!!openSections.relevance} onToggle={toggleSection}>
            <p className="text-[10px] text-adm-tertiary mb-2">Tag content so it ranks higher for matching subscribers.</p>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-[10px] text-adm-tertiary">Relevance Tags</Label>
                <Input value={form.relevance_tags} onChange={e => set('relevance_tags', e.target.value)} className="bg-adm-panel border-white/10 text-adm-primary h-8 text-xs" placeholder="parkinson, levodopa, gut_brain" data-testid="relevance-tags-input" /></div>
              <div><Label className="text-[10px] text-adm-tertiary">Target Condition</Label>
                <Input value={form.target_condition} onChange={e => set('target_condition', e.target.value)} className="bg-adm-panel border-white/10 text-adm-primary h-8 text-xs" placeholder="e.g. parkinson" /></div>
              <div><Label className="text-[10px] text-adm-tertiary">Target Phase</Label>
                <Input value={form.target_phase} onChange={e => set('target_phase', e.target.value)} className="bg-adm-panel border-white/10 text-adm-primary h-8 text-xs" placeholder="e.g. early, mid" /></div>
              <div><Label className="text-[10px] text-adm-tertiary">Priority Weight</Label>
                <Input type="number" value={form.priority_weight} onChange={e => set('priority_weight', parseInt(e.target.value) || 0)} className="bg-adm-panel border-white/10 text-adm-primary h-8 text-xs" /></div>
              <div><Label className="text-[10px] text-adm-tertiary">Symptom Patterns</Label>
                <Input value={form.target_symptom_patterns} onChange={e => set('target_symptom_patterns', e.target.value)} className="bg-adm-panel border-white/10 text-adm-primary h-8 text-xs" placeholder="tremor, freezing" /></div>
              <div><Label className="text-[10px] text-adm-tertiary">Medication Patterns</Label>
                <Input value={form.target_medication_patterns} onChange={e => set('target_medication_patterns', e.target.value)} className="bg-adm-panel border-white/10 text-adm-primary h-8 text-xs" placeholder="adaptive_timing" /></div>
            </div>
          </Section>

          {/* ═══ SLIDES ═══ */}
          <Section id="slides" label={`Slides (${slideImages.length})`} icon={Image} open={!!openSections.slides} onToggle={toggleSection} count={slideImages.length || null}>
            {slideImages.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-3">
                {slideImages.map((url, i) => (
                  <div key={i} className="w-20 h-14 rounded-md overflow-hidden bg-white/5 border border-white/[0.06] relative group">
                    <img src={url.startsWith('http') ? url : url.startsWith('/api/') ? url : `${API}/documents/file/${url}`} alt={`Slide ${i + 1}`} className="w-full h-full object-cover" />
                    <span className="absolute bottom-0 right-0 bg-black/50 text-white text-[8px] px-1">{i + 1}</span>
                  </div>
                ))}
              </div>
            )}
            {!effectiveContentId && <p className="text-[10px] text-amber-400 mb-2">Save as draft first to enable uploads</p>}
            <div className="flex gap-2 flex-wrap">
              <input ref={pptxInputRef} type="file" className="hidden" accept=".pptx,.ppt" onChange={handlePptxUpload} data-testid="pptx-upload-input" />
              <Button variant="outline" size="sm" type="button"
                onClick={() => pptxInputRef.current?.click()} disabled={uploading || !effectiveContentId}
                className="border-purple-500/20 text-purple-400 hover:bg-purple-500/10 text-xs h-8" data-testid="pptx-upload-btn">
                {uploading ? <Loader2 className="w-3 h-3 mr-1.5 animate-spin" /> : <Upload className="w-3 h-3 mr-1.5" />}
                Upload PPTX
              </Button>
              <input ref={slideInputRef} type="file" className="hidden" accept="image/*" multiple onChange={handleSlideUpload} data-testid="slide-upload-input" />
              <Button variant="outline" size="sm" type="button"
                onClick={() => slideInputRef.current?.click()} disabled={uploading || !effectiveContentId}
                className="border-white/10 text-adm-secondary text-xs h-8">
                <Upload className="w-3 h-3 mr-1.5" /> Upload Images
              </Button>
            </div>
          </Section>

          {/* ═══ AUDIO ═══ */}
          <Section id="audio" label={audioUrl ? 'Audio (attached)' : 'Audio'} icon={Music} open={!!openSections.audio} onToggle={toggleSection}>
            {audioUrl ? (
              <div className="flex items-center gap-2 p-2 rounded-lg bg-emerald-500/5 border border-emerald-500/10 mb-2">
                <Music className="w-4 h-4 text-emerald-400" />
                <span className="text-xs text-emerald-400 flex-1">Audio file attached</span>
                <Button variant="ghost" size="sm" className="h-6 text-[10px] text-adm-tertiary" onClick={() => setAudioUrl('')}>Remove</Button>
              </div>
            ) : null}
            {!effectiveContentId && <p className="text-[10px] text-amber-400 mb-2">Save as draft first to enable uploads</p>}
            <input ref={audioInputRef} type="file" className="hidden" accept=".mp3,.m4a,.wav,.ogg,.mpeg" onChange={handleAudioUpload} data-testid="audio-upload-input" />
            <Button variant="outline" size="sm" type="button"
              onClick={() => audioInputRef.current?.click()} disabled={uploading || !effectiveContentId}
              className="border-white/10 text-adm-secondary text-xs h-8" data-testid="audio-upload-btn">
              <Music className="w-3 h-3 mr-1.5" /> {audioUrl ? 'Replace Audio' : 'Upload Audio (.mp3, .m4a, .wav)'}
            </Button>
          </Section>

          {/* ═══ KEY TAKEAWAYS ═══ */}
          <Section id="takeaways" label={`Key Takeaways (${form.takeaways.filter(t => t.trim()).length})`} icon={GripVertical} open={!!openSections.takeaways} onToggle={toggleSection}>
            <div className="space-y-2">
              {form.takeaways.map((t, i) => (
                <div key={`tk-${i}`} className="flex items-center gap-2">
                  <span className="text-[10px] text-adm-tertiary w-4">{i + 1}</span>
                  <Input value={t} onChange={e => updateTakeaway(i, e.target.value)} placeholder={`Takeaway ${i + 1}`}
                    className="bg-adm-panel border-white/10 text-adm-primary h-8 text-xs flex-1" data-testid={`takeaway-${i}`} />
                  {form.takeaways.length > 1 && <button type="button" onClick={() => removeTakeaway(i)} className="p-1 text-adm-tertiary hover:text-rose-400"><X className="w-3 h-3" /></button>}
                </div>
              ))}
              {form.takeaways.length < 5 && <button type="button" onClick={addTakeaway} className="text-[10px] text-adm-accent hover:text-adm-accent/80">+ Add takeaway</button>}
            </div>
          </Section>

          {/* ═══ ARTICLE SECTIONS ═══ */}
          <Section id="sections" label={`Article Sections (${form.sections.filter(s => s.body?.trim()).length})`} icon={FileText} open={!!openSections.sections} onToggle={toggleSection}>
            {/* Mode toggle */}
            <div className="flex gap-2 mb-3">
              <button type="button" onClick={() => setArticleMode('manual')}
                className={`px-3 py-1.5 rounded-full text-xs transition-colors ${articleMode === 'manual' ? 'bg-adm-accent/15 text-adm-accent' : 'text-adm-tertiary hover:text-adm-secondary'}`}
                data-testid="article-mode-manual">
                Build Manually
              </button>
              <button type="button" onClick={() => setArticleMode('paste')}
                className={`px-3 py-1.5 rounded-full text-xs transition-colors ${articleMode === 'paste' ? 'bg-adm-accent/15 text-adm-accent' : 'text-adm-tertiary hover:text-adm-secondary'}`}
                data-testid="article-mode-paste">
                Paste Full Article
              </button>
            </div>

            {articleMode === 'paste' ? (
              <div className="space-y-3">
                <Textarea value={pastedArticle} onChange={e => setPastedArticle(e.target.value)}
                  placeholder="Paste your full article here. The system will auto-detect headings or split into default sections (What is happening, Biological explanation, Why it matters, What to observe, What improves)..."
                  className="bg-adm-panel border-white/10 text-adm-primary text-xs min-h-[160px]" data-testid="paste-article-input" />
                <Button variant="outline" size="sm" type="button" onClick={handleAutoStructure} disabled={autoStructuring || !pastedArticle.trim()}
                  className="border-adm-accent/20 text-adm-accent text-xs h-8" data-testid="auto-structure-btn">
                  <Wand2 className="w-3 h-3 mr-1.5" /> Auto Structure Article
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                {form.sections.map((s, i) => (
                  <div key={`sec-${i}`} className="space-y-1.5 p-2.5 rounded-lg bg-white/[0.02]">
                    <div className="flex items-center gap-2">
                      <Input value={s.title} onChange={e => updateSection(i, 'title', e.target.value)} placeholder={`Section ${i + 1} title`}
                        className="bg-adm-panel border-white/10 text-adm-primary h-7 text-xs flex-1" data-testid={`section-title-${i}`} />
                      {form.sections.length > 1 && <button type="button" onClick={() => removeSection(i)} className="p-1 text-adm-tertiary hover:text-rose-400"><X className="w-3 h-3" /></button>}
                    </div>
                    <Textarea value={s.body} onChange={e => updateSection(i, 'body', e.target.value)} placeholder="Section content..."
                      className="bg-adm-panel border-white/10 text-adm-primary text-xs min-h-[60px]" data-testid={`section-body-${i}`} />
                  </div>
                ))}
                {form.sections.length < 6 && <button type="button" onClick={addSection} className="text-[10px] text-adm-accent hover:text-adm-accent/80">+ Add section</button>}
              </div>
            )}
          </Section>
        </div>

        {/* Action Bar */}
        <div className="flex items-center justify-between px-5 py-3 border-t border-white/[0.06] bg-white/[0.01] sticky bottom-0">
          <Button variant="ghost" type="button" onClick={onClose} className="text-adm-tertiary text-xs">Cancel</Button>
          <div className="flex gap-2">
            <Button variant="outline" type="button" onClick={() => handleSave(false)} disabled={saving}
              className="border-white/10 text-adm-secondary text-xs" data-testid="save-draft-btn">
              {saving ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Save className="w-3 h-3 mr-1" />} Save Draft
            </Button>
            <Button type="button" onClick={() => handleSave(true)} disabled={saving}
              className="bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30 border-0 text-xs" data-testid="publish-btn">
              {saving ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Send className="w-3 h-3 mr-1" />} Publish
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
